import React, { useMemo } from 'react';
import { getLast365Days, getMonthLabel } from '../utils/dates';

export default function CalendarHeatmap({ checkinDates, color = '#6366F1' }) {
  const days = useMemo(() => getLast365Days(), []);
  const dateSet = useMemo(() => new Set(checkinDates || []), [checkinDates]);

  // Calculate grid layout (52-53 weeks of 7 days)
  const weeks = useMemo(() => {
    const result = [];
    let week = [];
    // Pad the first week to align to the correct day
    const firstDay = new Date(days[0] + 'T00:00:00').getDay();
    for (let i = 0; i < firstDay; i++) {
      week.push(null);
    }
    for (const day of days) {
      week.push(day);
      if (week.length === 7) {
        result.push(week);
        week = [];
      }
    }
    if (week.length > 0) {
      result.push(week);
    }
    return result;
  }, [days]);

  // Month labels
  const monthLabels = useMemo(() => {
    const labels = [];
    let lastMonth = '';
    weeks.forEach((week, i) => {
      const firstDay = week.find((d) => d !== null);
      if (firstDay) {
        const month = getMonthLabel(firstDay);
        if (month !== lastMonth) {
          labels.push({ month, index: i });
          lastMonth = month;
        }
      }
    });
    return labels;
  }, [weeks]);

  const cellSize = 11;
  const gap = 2;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 overflow-x-auto">
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
        Activity
      </h3>
      <svg
        width={weeks.length * (cellSize + gap) + 30}
        height={7 * (cellSize + gap) + 20}
        className="text-gray-300 dark:text-gray-600"
      >
        {/* Month labels */}
        {monthLabels.map(({ month, index }) => (
          <text
            key={`${month}-${index}`}
            x={index * (cellSize + gap) + 30}
            y={10}
            className="fill-gray-400 dark:fill-gray-500"
            fontSize={10}
          >
            {month}
          </text>
        ))}

        {/* Day labels */}
        {['', 'Mon', '', 'Wed', '', 'Fri', ''].map((label, i) => (
          <text
            key={i}
            x={0}
            y={i * (cellSize + gap) + 28}
            className="fill-gray-400 dark:fill-gray-500"
            fontSize={9}
            dominantBaseline="middle"
          >
            {label}
          </text>
        ))}

        {/* Grid — completed days use the habit colour; missed days a visible
            neutral fill (via the CSS class); padding cells are transparent. */}
        {weeks.map((week, wi) =>
          week.map((day, di) => {
            const completed = day && dateSet.has(day);
            return (
              <rect
                key={`${wi}-${di}`}
                x={wi * (cellSize + gap) + 30}
                y={di * (cellSize + gap) + 16}
                width={cellSize}
                height={cellSize}
                rx={2}
                fill={completed ? color : 'transparent'}
                className={!day ? '' : completed ? '' : 'fill-gray-200 dark:fill-gray-700'}
              >
                {day && <title>{day}: {completed ? 'Completed' : 'Missed'}</title>}
              </rect>
            );
          })
        )}
      </svg>
    </div>
  );
}
